import styles from './General.module.scss';

import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@work4all/components/lib/dataDisplay/card';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { ReceiptKindInternal } from '@work4all/models/lib/Enums/ReceiptKindInternal.enum';

import { currencyAsSign } from '@work4all/utils';

import { LedgerAccountPickerField } from '../../../../../../components/entity-picker/LedgerAccountPickerField';
import { TaxKeyPickerField } from '../../../../../../components/entity-picker/TaxKeyPickerField';
import { VatPickerField } from '../../../../../../components/entity-picker/VatPickerField';
import { ControllerPlus } from '../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { Collapse, ControlWrapper } from '../../../components';
import { CurrencyExchangeInfoContext } from '../../inbound-invoice/currency-exchange-info-context';
import { TravelReceiptsFormValue } from '../type';

// PRIVATE_VEHICLE (Nutzung Privat-KfZ.)
const PRIVATE_VEHICLE_TRAVEL_COST = 6;

const LEDGER_ACCOUNT_PREFILTER = [
  {
    kind: {
      $in: [0, 4, 10],
    },
  },
  {
    disabled: {
      $eq: false,
    },
  },
];

export const Costs = () => {
  const { t } = useTranslation();

  const form = useFormContextPlus<TravelReceiptsFormValue>();
  const { control, watch } = form;

  const receiptKindTravelCost = watch('receiptKindTravelCost');
  const receiptKind = watch('receiptKind');

  return (
    <Collapse
      title={t('SETTINGS.PROJECT_TIME.COSTS')}
      className={styles.costs}
      defaultOpen
    >
      <Card>
        {!(
          receiptKindTravelCost?.enumReceiptKindTravelCosts ===
            PRIVATE_VEHICLE_TRAVEL_COST &&
          receiptKind?.typeOfReceiptType === ReceiptKindInternal.FAHRTKOSTEN
        ) ? (
          <>
            <ControlWrapper gridTemplateColumns="2fr 1fr 1fr 3fr">
              <ControllerPlus
                name="amountGross1"
                control={control}
                render={({ field }) => {
                  return (
                    <NumberInput
                      {...field}
                      step={0.01}
                      hideControls
                      label={t('COMMON.GROSS')}
                    />
                  );
                }}
              />
              <ControllerPlus
                name="taxKey1"
                control={control}
                render={({ field }) => {
                  return (
                    <TaxKeyPickerField
                      {...field}
                      view="field"
                      label={t('COMMON.TAX_KEY')}
                    />
                  );
                }}
              />
              <ControllerPlus
                name="vat1"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <VatPickerField
                      {...field}
                      error={fieldState?.error?.message}
                    />
                  );
                }}
              />
              <ControllerPlus
                name="ledgerAccount1"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <LedgerAccountPickerField
                      {...field}
                      label={t('BOOKINGS_TABLE.COLUMNS.LEDGER_ACCOUNT')}
                      error={fieldState?.error?.message}
                      prefilter={LEDGER_ACCOUNT_PREFILTER}
                    />
                  );
                }}
              />
            </ControlWrapper>
            <ControlWrapper gridTemplateColumns="2fr 1fr 1fr 3fr">
              <ControllerPlus
                name="amountGross2"
                control={control}
                render={({ field }) => {
                  return (
                    <NumberInput
                      {...field}
                      step={0.01}
                      hideControls
                      label={t('COMMON.GROSS')}
                    />
                  );
                }}
              />

              <ControllerPlus
                name="taxKey2"
                control={control}
                render={({ field }) => {
                  return (
                    <TaxKeyPickerField
                      {...field}
                      view="field"
                      label={t('COMMON.TAX_KEY')}
                    />
                  );
                }}
              />

              <ControllerPlus
                name="vat2"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <VatPickerField
                      {...field}
                      error={fieldState?.error?.message}
                    />
                  );
                }}
              />

              <ControllerPlus
                name="ledgerAccount2"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <LedgerAccountPickerField
                      {...field}
                      label={t('BOOKINGS_TABLE.COLUMNS.LEDGER_ACCOUNT')}
                      error={fieldState?.error?.message}
                      prefilter={LEDGER_ACCOUNT_PREFILTER}
                    />
                  );
                }}
              />
            </ControlWrapper>
            <ControlWrapper gridTemplateColumns="2fr 1fr 1fr 3fr">
              <ControllerPlus
                name="amountGross3"
                control={control}
                render={({ field }) => {
                  return (
                    <NumberInput
                      {...field}
                      step={0.01}
                      hideControls
                      label={t('COMMON.GROSS')}
                    />
                  );
                }}
              />

              <ControllerPlus
                name="taxKey3"
                control={control}
                render={({ field }) => {
                  return (
                    <TaxKeyPickerField
                      {...field}
                      view="field"
                      label={t('COMMON.TAX_KEY')}
                    />
                  );
                }}
              />

              <ControllerPlus
                name="vat3"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <VatPickerField
                      {...field}
                      error={fieldState?.error?.message}
                    />
                  );
                }}
              />

              <ControllerPlus
                name="ledgerAccount3"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <LedgerAccountPickerField
                      {...field}
                      label={t('BOOKINGS_TABLE.COLUMNS.LEDGER_ACCOUNT')}
                      error={fieldState?.error?.message}
                      prefilter={LEDGER_ACCOUNT_PREFILTER}
                    />
                  );
                }}
              />
            </ControlWrapper>
          </>
        ) : (
          <PrivateVehicleCost />
        )}
      </Card>
    </Collapse>
  );
};

const PrivateVehicleCost = () => {
  const { t } = useTranslation();

  const form = useFormContextPlus<TravelReceiptsFormValue>();
  const { control } = form;
  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  return (
    <ControlWrapper gridTemplateColumns="1fr 1fr 1fr 1fr">
      <ControllerPlus
        name="amount"
        control={control}
        render={({ field }) => {
          return (
            <NumberInput
              {...field}
              step={1}
              min={0}
              decimals={0}
              autoSelect
              hideControls
              label={t('COMMON.AMOUNT_KM')}
            />
          );
        }}
      />

      <ControllerPlus
        name="ePrice"
        control={control}
        render={({ field }) => {
          return (
            <NumberInput
              {...field}
              decimals={2}
              min={0}
              autoSelect
              step={0.01}
              hideControls
              label={t('COMMON.CURRENCY_PER_KM', {
                current: currencyAsSign(defaultCurrency.name),
              })}
            />
          );
        }}
      />

      <ControllerPlus
        name="expenditure"
        control={control}
        render={({ field }) => {
          return (
            <NumberInput
              {...field}
              decimals={2}
              truncate
              step={0.01}
              disabled
              hideControls
              label={t('COMMON.ACCOUNT_KM')}
            />
          );
        }}
      />

      <ControllerPlus
        name="ledgerAccount1"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <LedgerAccountPickerField
              {...field}
              label={t('BOOKINGS_TABLE.COLUMNS.LEDGER_ACCOUNT')}
              error={fieldState?.error?.message}
              prefilter={LEDGER_ACCOUNT_PREFILTER}
            />
          );
        }}
      />
    </ControlWrapper>
  );
};
